import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DataService } from '../../services/data.service';
import { CollectionGet } from '../../models/collection';

export interface NewProjectData {
  name: string;
  description?: string;
  tags?: string;
  collection?: string;
  availableCollections?: { id: string; name: string; }[];
  isEdit?: boolean;
  projectId?: string;
}

@Component({
  selector: 'app-new-project-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule
  ],
  template: `
    <div class="app-dialog">
      <div class="app-dialog__heading">
        <button
          mat-icon-button
          type="button"
          class="new-project-dialog-close"
          aria-label="Close"
          (click)="onCancel()">
          <mat-icon>close</mat-icon>
        </button>

        <div class="app-dialog__heading-copy">
          <span class="app-dialog__eyebrow">Project</span>
          <div class="app-dialog__title-row">
            <div class="app-dialog__title-copy">
              <h2 mat-dialog-title class="app-dialog__title">{{ data.isEdit ? 'Edit Project' : 'New Project' }}</h2>
              <p>Set up the project details and keep your analysis organized.</p>
            </div>
          </div>
        </div>
      </div>

      <form (ngSubmit)="data.isEdit ? onSave() : onCreate()" novalidate>
        <mat-dialog-content class="app-dialog__content">
          <section class="app-dialog__section">
            <div class="app-dialog__section-heading">
              <h3>Project details</h3>
            </div>

            <div class="app-dialog__grid">
              <mat-form-field appearance="outline" class="app-dialog__full-width">
                <mat-label>Project Name</mat-label>
                <input matInput name="projectName" [(ngModel)]="data.name" placeholder="Enter project name" required>
              </mat-form-field>

              <mat-form-field appearance="outline" class="app-dialog__full-width">
                <mat-label>Project Description (optional)</mat-label>
                <textarea matInput name="projectDescription" [(ngModel)]="data.description" rows="4" placeholder="Enter project description"></textarea>
              </mat-form-field>

              <mat-form-field appearance="outline" class="app-dialog__full-width">
                <mat-label>Tags (optional)</mat-label>
                <input matInput name="projectTags" [(ngModel)]="data.tags" placeholder="Enter tags separated by commas">
              </mat-form-field>
            </div>
          </section>
        </mat-dialog-content>

        <mat-dialog-actions class="app-dialog__actions" align="end">
          <button mat-button type="button" (click)="onCancel()">Cancel</button>
          <button mat-raised-button color="primary" type="submit">{{ data.isEdit ? 'Save' : 'Create' }}</button>
        </mat-dialog-actions>
      </form>
    </div>
  `,
  styles: [`
    .new-project-dialog-close {
      position: absolute !important;
      top: 12px !important;
      right: 16px !important;
      z-index: 1;
      color: var(--text-secondary);
    }
  `]
})
export class NewProjectDialogComponent implements OnInit {
  availableCollections: CollectionGet[] = [];
  isLoadingCollections = false;
  collectionsError = '';

  constructor(
    public dialogRef: MatDialogRef<NewProjectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: NewProjectData,
    private dataService: DataService
  ) {
    // Initialize empty data if not provided
    if (!this.data) {
      this.data = {
        name: '',
        description: '',
        tags: '',
        collection: ''
      };
    }
  }

  ngOnInit(): void {
    this.loadCollections();
  }

  loadCollections(): void {
    this.isLoadingCollections = true;
    this.collectionsError = '';

    this.dataService.getCollectionList().subscribe({
      next: (collections) => {
        this.availableCollections = collections;
        this.isLoadingCollections = false;
      },
      error: (error) => {
        console.error('Error loading collections:', error);
        this.collectionsError = 'Failed to load collections. Please try again.';
        this.isLoadingCollections = false;
      }
    });
  }

  onCreate(): void {
    if (this.data.name?.trim()) {
      this.dialogRef.close({ action: 'create', data: this.data });
    }
  }

  onSave(): void {
    if (this.data.name?.trim()) {
      this.dialogRef.close({ action: 'save', data: this.data });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
